/* The app chrome: brand, the six destinations, and the account menu.
 *
 * Who is signed in now comes from the session — the account menu shows the real
 * name, email and membership behind the sign-in.
 *
 * The menu also carries the appearance switch and a role preview, so the
 * permission rules in IDEA.md §5 can be seen working rather than described. The
 * preview changes what this build draws, not what the server allows; it goes
 * away when jobs move to the database and the server is the only thing deciding. */
import { useEffect, type ReactNode } from 'react';
import { ROLE_LABEL, ROLES } from '@oneshot2pac/shared';
import { ROLE_NOTE } from '@/data/people';
import { useApp } from '@/store/useApp';
import type { Role, Screen } from '@/types';
import { Seg } from './controls';
import { CHROME_TIGHT, useNarrowerThan, usePageGutter, withGutter } from './viewport';

const TABS: { label: string; key: Screen }[] = [
  { label: 'Dashboard', key: 'dashboard' },
  { label: 'Board', key: 'board' },
  { label: 'Jobs', key: 'jobs' },
  { label: 'Schedule', key: 'schedule' },
  { label: 'Customers', key: 'customers' },
  { label: 'Settings', key: 'settings' },
];

export function Shell({ children }: { children: ReactNode }) {
  const screen = useApp((s) => s.screen);
  const origin = useApp((s) => s.origin);
  const menuOpen = useApp((s) => s.menuOpen);
  const role = useApp((s) => s.role);
  const openChangePassword = useApp((s) => s.openChangePassword);
  const theme = useApp((s) => s.theme);
  const session = useApp((s) => s.session);
  // The one number in the chrome. It rides on the Dashboard tab from wherever
  // you are, which is the whole reason the board is read on the way in rather
  // than when that screen opens.
  const unread = useApp((s) => s.unread);
  const go = useApp((s) => s.go);
  const setTheme = useApp((s) => s.setTheme);
  const signOut = useApp((s) => s.signOut);
  const set = useApp((s) => s.set);

  // The chrome is the whole app's width floor: nothing else here has a size it
  // cannot give back, so this is the one measurement the shell takes.
  const tight = useNarrowerThan(CHROME_TIGHT);
  // The app's own gutter, shared with every page under this header so the two
  // line up at any width. It comes in earlier than `tight` does — see viewport.
  const gutter = usePageGutter();

  /* Identity is the real membership; the role below it may be a preview. The
     two are shown separately on purpose — the account block should always say
     who you actually are. */
  const realRole: Role = session?.membership.role ?? 'staff';
  const me = {
    initials: session?.user.initials ?? '··',
    name: session?.user.name ?? 'Signed in',
    email: session?.user.email ?? '',
    access: `${ROLE_LABEL[realRole]} · ${
      session?.membership.financialAccess ? 'money access' : 'no money access'
    }`,
  };
  const previewing = role !== realRole;

  // Escape closes the menu — it is a popover, and a keyboard user should not
  // have to find the scrim.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') set('menuOpen', false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, set]);

  // A detail screen keeps its parent tab lit, so you always know where you are.
  const detailHome = origin === 'customer' ? 'customers' : origin;
  const isActive = (key: Screen) =>
    screen === key ||
    ((screen === 'job' || screen === 'items') && detailHome === key) ||
    (key === 'customers' && screen === 'customer');

  return (
    <div
      style={{
        // The document is pinned to the viewport in components.css, so the
        // shell is simply all of it. A dvh here would be a second answer to
        // the same question, and on iOS a different one.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--ground)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tight ? 14 : 24,
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          padding: withGutter(gutter, 0),
          height: 60,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--line)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginRight: 4, flex: 'none' }}
        >
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            1shot2pac
          </span>
          {/* The city is the one thing up here nobody navigates by, so it is
              the first thing to go when the screen is short of width. */}
          {tight ? null : (
            <span
              style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-muted)' }}
            >
              Brisbane
            </span>
          )}
        </div>

        <nav
          aria-label="Sections"
          style={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            marginRight: 'auto',
            flex: 'none',
          }}
        >
          {TABS.map((t) => {
            const on = isActive(t.key);
            // Only the dashboard carries one, and only when there is something
            // to carry. A badge showing 0 is a badge that has stopped meaning
            // anything is new.
            const badge = t.key === 'dashboard' && unread > 0 ? unread : 0;
            return (
              <button
                key={t.key}
                type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => go(t.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  minHeight: 40,
                  padding: tight ? '0 10px' : '0 16px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  background: on ? 'var(--inv-bg)' : 'transparent',
                  color: on ? 'var(--inv-ink)' : 'var(--ink)',
                  border: `1px solid ${on ? 'var(--ink)' : 'transparent'}`,
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {t.label}
                {badge ? <UnreadCount count={badge} on={on} /> : null}
              </button>
            );
          })}
        </nav>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 'none',
          }}
        >
          <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-muted)' }}>
            {ROLE_LABEL[role]}
            {previewing ? ' (preview)' : ''}
          </span>
          <button
            type="button"
            title="Account"
            aria-label="Account"
            aria-expanded={menuOpen}
            onClick={() => set('menuOpen', !menuOpen)}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: `1px solid ${menuOpen ? 'var(--ink)' : 'var(--line)'}`,
              background: menuOpen ? 'var(--ground)' : 'var(--surface)',
              color: 'var(--ink)',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {me.initials}
          </button>

          {menuOpen ? (
            <>
              <div
                onClick={() => set('menuOpen', false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div
                className="blueprint"
                style={{
                  position: 'absolute',
                  top: 54,
                  right: 0,
                  zIndex: 50,
                  width: 302,
                  padding: 16,
                  background: 'var(--surface)',
                  boxShadow: '0 10px 30px var(--shade)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 15,
                  whiteSpace: 'normal',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      flex: 'none',
                      borderRadius: '50%',
                      border: '1px solid var(--line)',
                      background: 'var(--ground)',
                      fontFamily: 'var(--font-data)',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {me.initials}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{me.name}</div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {me.email}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 11,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {me.access}
                    </div>
                  </div>
                </div>

                <MenuGroup label="Appearance">
                  <Seg
                    height={44}
                    options={[
                      { label: 'Light', on: theme === 'light', pick: () => setTheme('light') },
                      { label: 'Dark', on: theme === 'dark', pick: () => setTheme('dark') },
                    ]}
                  />
                </MenuGroup>

                <MenuGroup label="View as">
                  <Seg
                    height={44}
                    options={ROLES.map((r) => ({
                      label: ROLE_LABEL[r],
                      on: role === r,
                      pick: () => set('role', r),
                    }))}
                  />
                  <span style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.4 }}>
                    {ROLE_NOTE[role]}
                    {previewing
                      ? ` You are signed in as ${ROLE_LABEL[realRole].toLowerCase()} — this only changes what you see.`
                      : ''}
                  </span>
                </MenuGroup>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    borderTop: '1px solid var(--line-soft)',
                    paddingTop: 12,
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      set('menuOpen', false);
                      go('settings');
                    }}
                    style={{ height: 48, justifyContent: 'flex-start' }}
                  >
                    Settings
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={openChangePassword}
                    style={{ height: 48, justifyContent: 'flex-start' }}
                  >
                    Change password
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      void signOut();
                    }}
                    style={{ height: 48, justifyContent: 'flex-start', color: 'var(--urgent)' }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </header>

      {/* The viewport is the frame: the chrome is fixed and this is the only
          thing that scrolls. The board depends on that — its columns are sized
          against a real height, so its own horizontal scrollbar stays on screen
          instead of being pushed below the fold by a page that grew. Every
          other screen just scrolls vertically in here as before.

          Sideways it does not scroll at all. A dense list — the jobs table, the
          board's columns, the schedule grid, the settings lists — scrolls
          inside its own container, which is what a table and a kanban are for;
          what must never happen is the app itself sliding under a finger on an
          iPad because one panel was 40px wider than the screen. `contain` is
          the same rule at the other end: reaching the bottom of this stops
          here rather than handing the drag to the document behind it. */}
      <main
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
        }}
      >
        {children}
      </main>
    </div>
  );
}

/** How many notices are new, on the tab that leads to them.
 *
 *  The number is inside the button rather than floating over its corner: the
 *  chrome is one row of pills and a badge hanging off one of them would be the
 *  only thing in the app that overlaps something else. It reads as part of the
 *  label, which is what it is.
 *
 *  Never colour alone (IDEA.md §10) — the count is a numeral, and the label
 *  beside it is what the numeral counts. `aria-label` says both in a sentence,
 *  because "Dashboard 3" is not what a screen reader should announce. */
function UnreadCount({ count, on }: { count: number; on: boolean }) {
  return (
    <span
      aria-label={`${count} new ${count === 1 ? 'notice' : 'notices'}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 19,
        height: 19,
        padding: '0 5px',
        borderRadius: 999,
        // The urgent colour is what the app already reserves for "look at
        // this", and this is the only place in the chrome that uses it.
        background: on ? 'var(--inv-ink)' : 'var(--urgent)',
        color: on ? 'var(--inv-bg)' : 'var(--on-urgent)',
        fontFamily: 'var(--font-data)',
        fontSize: 10.5,
        fontWeight: 500,
        letterSpacing: 0,
      }}
    >
      {count}
    </span>
  );
}

function MenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <span
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--ink-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}
